import type { DepartureCityType } from '@/features/departure-city';
import type { Country, Selection } from '@/features/destination';
import type { DateFrom } from '@/features/date-from';
import type { NightsRange } from '@/features/nights-range';
import type { ApiCountryPosition, ApiTourCriteria } from '../api/getSearchFormData.types';

export interface CityTypeCriteria {
  nights?: NightsRange;
  dateFrom?: DateFrom;
}

export interface CountryCriteria {
  capital?: CityTypeCriteria;
  region?: CityTypeCriteria;
}

export type DefaultCriteriaIndex = Record<number, CountryCriteria>;

export interface ResolvedCriteria {
  nights?: NightsRange;
  dateFrom?: DateFrom;
}

function mapCriteria(raw: ApiTourCriteria | undefined): CityTypeCriteria | undefined {
  if (!raw) {
    return undefined;
  }

  const result: CityTypeCriteria = {};

  const nf = Number(raw.nightRange?.from);
  const nt = Number(raw.nightRange?.to);
  if (Number.isFinite(nf) && Number.isFinite(nt)) {
    result.nights = { from: nf, to: nt };
  }

  const dFrom = raw.checkInDateRange?.from;
  const dTo = raw.checkInDateRange?.to;
  if (typeof dFrom === 'string' && dFrom !== '' && typeof dTo === 'string' && dTo !== '') {
    const flex = Number(raw.checkInDateRange?.daysFlexible);
    result.dateFrom = { from: dFrom, to: dTo, daysFlexible: Number.isFinite(flex) ? flex : 0 };
  }

  return result.nights || result.dateFrom ? result : undefined;
}

export function buildDefaultCriteriaIndex(positions: ApiCountryPosition[]): DefaultCriteriaIndex {
  const index: DefaultCriteriaIndex = {};

  for (const pos of positions) {
    const id = Number(pos.country?.id);
    if (!Number.isFinite(id)) {
      continue;
    }

    const capital = mapCriteria(pos.capitalTourCriteria);
    const region = mapCriteria(pos.regionTourCriteria);
    if (!capital && !region) {
      continue;
    }

    const entry: CountryCriteria = {};
    if (capital) {
      entry.capital = capital;
    }
    if (region) {
      entry.region = region;
    }
    index[id] = entry;
  }

  return index;
}

export function resolveDefaultCriteria(
  index: DefaultCriteriaIndex,
  countryId: number | undefined,
  cityType?: DepartureCityType,
): ResolvedCriteria {
  if (countryId === undefined || !Number.isFinite(countryId)) {
    return {};
  }
  const country = index[countryId];
  if (!country) {
    return {};
  }

  if (cityType === 'capital') {
    const block = country.capital;
    if (!block) {
      return {};
    }
    return { nights: block.nights, dateFrom: block.dateFrom };
  }

  return country.region?.nights ? { nights: country.region.nights, dateFrom: country.region.dateFrom } : {};
}

export function resolveInitialDateFrom(
  index: DefaultCriteriaIndex,
  countryId: number | undefined,
  cityType?: DepartureCityType,
): DateFrom | undefined {
  if (countryId === undefined || !Number.isFinite(countryId)) {
    return undefined;
  }
  const country = index[countryId];
  if (!country) {
    return undefined;
  }
  const primary = cityType === 'region' ? country.region : country.capital;
  const fallback = cityType === 'region' ? country.capital : country.region;
  return primary?.dateFrom ?? fallback?.dateFrom;
}

export function resolveSourceCountryId(
  destination: Selection | null,
  countries: Country[],
): number | undefined {
  if (destination && destination.kind !== 'none') {
    return destination.country.id;
  }

  let best: Country | undefined;
  for (const c of countries) {
    if (!best) {
      best = c;
      continue;
    }
    const bp = best.position ?? Number.POSITIVE_INFINITY;
    const cp = c.position ?? Number.POSITIVE_INFINITY;
    if (cp < bp) {
      best = c;
    }
  }
  return best?.id;
}

export function nightsEqual(a: NightsRange | null, b: NightsRange | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.from === b.from && a.to === b.to;
}

export function dateFromEqual(a: DateFrom | null, b: DateFrom | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.from === b.from && a.to === b.to && a.daysFlexible === b.daysFlexible;
}
